import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { saveAs } from 'file-saver';
import { firstValueFrom, filter } from 'rxjs';

import { VgApiService } from '@videogular/ngx-videogular/core';
import { VideoStreamingService } from 'src/app/features/video/controller/video-streaming.service';
import { VideoStatusService } from 'src/app/features/video/controller/video-status.service';
import { VideoSearchResult } from 'src/app/features/video/model/video-search-result.model';
import { PlayModelOptions, VideoSceneService } from 'src/app/features/video/controller/video-scene.service';
import {
  VideoSceneResponse,
  VideoSceneUpdateRequest,
  LabService,
  TeamOptionResponse,
  Season,
  StatisticsService
} from "@solverml/api";

import { VideoPlayerComponent } from '../../features/video/view/video-player/video-player.component';
import { SceneFilterComponent, FilterChangeEvent } from "../../features/video/view/video-player/addons/scene-selector/scene-filter/scene-filter.component";
import { ActiveFiltersDisplayComponent, ActiveFilterChip } from "../../features/video/view/video-player/addons/scene-selector/active-filters-display/active-filters-display.component";
import { ScenePlayerComponent } from '../../features/video/view/video-player/addons/scene-player/scene-player.component';
import { SceneTableComponent } from '../../features/video/view/video-player/addons/scene-table/scene-table.component';
import { ConfirmationModalComponent, ConfirmationModalData } from "../../fragments/confirmation-modal/confirmation-modal.component";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RelaunchPipelineResponse, VideoLabService } from "../../features/video/controller/video-lab.service";
import { TeamCheckService } from "../../features/video/controller/team-check.service";
import { VideoQualityIndicatorComponent } from '../../features/video/view/video-quality-indicator/video-quality-indicator.component';
import {
  VideoBasicInfoModalComponent,
  VideoBasicInfoModalResult
} from "../../features/video/view/video-basic-info-modal/video-basic-info-modal.component";
import { FilterDataService } from '../../services/filter-data.service';

@Component({
  selector: 'app-video-watch-page',
  templateUrl: './video-watch-page.component.html',
  styleUrls: ['./video-watch-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    FormsModule,
    VideoPlayerComponent,
    SceneFilterComponent,
    MatTooltipModule,
    MatSnackBarModule,
    ActiveFiltersDisplayComponent,
    ScenePlayerComponent,
    SceneTableComponent,
    VideoQualityIndicatorComponent,
  ],
})
export class VideoWatchPageComponent implements OnInit {

  video: VideoSearchResult | null = null;
  isSceneLoaded = false;

  private playerApi: VgApiService | null = null;

  public isTwelveSecondsActive: boolean = false;

  public currentScene: VideoSceneResponse | null = null;
  private manuallySelectedScene: VideoSceneResponse | null = null;
  private isPlaying: boolean = false;
  private currentTime: number = 0;
  private hasRewoundOnce: boolean = false;
  private lastFilterIds: number[] = [];
  private lastTeamFilterId: string | null = null;
  private lastPlayModelQuery: string = '';

  public allScenes: VideoSceneResponse[] = [];
  public filteredScenes: VideoSceneResponse[] = [];
  public isEditingScene = false;

  playModelOptions: PlayModelOptions = {}
  public displayedActiveFilters: ActiveFilterChip[] = [];

  public showInternalButton = false;
  public isRelaunching = false;
  public availableTeams: TeamOptionResponse[] = [];
  public seasons: Season[] = [];
  public videoQualityColor: 'ROJO' | 'NARANJA' | 'VERDE' | null = null;
  public azimutalEnabled: -1 | 0 | 1 = -1;
  public allowedCompetitionOptions: { id: number; name: string }[] = [];
  public allowedCategoryOptions: { id: number; name: string }[] = [];
  public allowedGenderOptions: { id: number; name: string }[] = [];

  @ViewChild(VideoPlayerComponent) private videoPlayer!: VideoPlayerComponent;
  @ViewChild(SceneFilterComponent) private sceneFilter!: SceneFilterComponent;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private videoStreamingService: VideoStreamingService,
    private videoSceneService: VideoSceneService,
    private dialog: MatDialog,
    private labService: LabService,
    private snackBar: MatSnackBar,
    private videoLabService: VideoLabService,
    private teamCheckService: TeamCheckService,
    private videoStatusService: VideoStatusService,
    private statisticsService: StatisticsService,
    private filterDataService: FilterDataService
  ) {
    const state = this.router.getCurrentNavigation()?.extras.state as { video: VideoSearchResult };
    if (state?.video) {
      this.video = state.video;
    }
  }

  async ngOnInit() {
    const videoId = this.video?.id || this.route.snapshot.paramMap.get('id');
    if (videoId) {
      const fullVideoData = await this.videoStreamingService.getVideoById(videoId);
      if (fullVideoData) {
        this.video = fullVideoData;
      }
      await this.loadSeasons();
      await this.loadAllowedVideoFilters();
      await this.loadVideoBasicInfo(videoId);
    }

    try {
      this.playModelOptions = await firstValueFrom(this.labService.getPlayModelOptions());
    } catch (error) {
      console.error('Error al cargar opciones de filtro:', error);
      this.playModelOptions = {};
    }
    if (this.video?.id) {
      try {
        this.availableTeams = await this.teamCheckService.getAssignedTeams(this.video.id);
      } catch (e) {
        console.error("Error cargando equipos asignados", e);
        this.availableTeams = [];
      }
      try {
        this.videoStatusService.getVideoQuality(this.video.id).subscribe({
          next: (quality) => {
            if (quality && quality.quality_color) {
              this.videoQualityColor = quality.quality_color as 'ROJO' | 'NARANJA' | 'VERDE';
            }
            const azimutalEnabled = (quality as typeof quality & { azimutal_enabled?: number })?.azimutal_enabled;
            this.azimutalEnabled = azimutalEnabled === 0 || azimutalEnabled === 1 ? azimutalEnabled : -1;
          },
          error: (err) => console.error("Error cargando calidad video", err)
        });
      } catch (e) {

      }
    }

    this.videoLabService.checkInternalAccess().subscribe(hasAccess => {
      this.showInternalButton = hasAccess;
    });

    await this.loadAllScenes();
    this.syncDefaultTeamFilterOptions();
    this.isSceneLoaded = true;
    this.filteredScenes = this.allScenes;
  }

  async openBasicInfoModal(): Promise<void> {
    if (!this.video?.id) return;
    try {
      const basicInfo = await this.videoStreamingService.getVideoBasicInfo(this.video.id);

      const dialogRef = this.dialog.open(VideoBasicInfoModalComponent, {
        width: '520px',
        data: {
          title: basicInfo.title,
          seasonId: basicInfo.season_id ?? 1,
          matchDate: basicInfo.match_date ?? new Date().toISOString().slice(0, 10),
          seasons: this.seasons,
          selectedGender: basicInfo.gender ?? this.allowedGenderOptions[0]?.name ?? null,
          selectedCompetitionId: basicInfo.competition_id ?? null,
          selectedCategoryId: basicInfo.category_id ?? null,
          genderOptions: this.allowedGenderOptions,
          competitionOptions: this.allowedCompetitionOptions,
          categoryOptions: this.allowedCategoryOptions
        }
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      if (!result) return;

      const updated = await this.videoStreamingService.updateVideoBasicInfo(
        this.video.id,
        result as VideoBasicInfoModalResult
      );

      this.video = {
        ...this.video,
        title: updated.title,
        season_id: updated.season_id,
        match_date: updated.match_date,
        gender: updated.gender || this.video.gender,
        competition: this.resolveOptionName(this.allowedCompetitionOptions, updated.competition_id) || this.video.competition,
        category: this.resolveOptionName(this.allowedCategoryOptions, updated.category_id) || this.video.category
      };

      this.snackBar.open('Datos básicos del vídeo actualizados', 'OK', { duration: 2500 });
    } catch (error) {
      console.error('Error actualizando datos básicos del vídeo:', error);
      this.snackBar.open('No se pudieron actualizar los datos del vídeo', 'Cerrar', { duration: 4000 });
    }
  }

  async toggleTwelveSecondsMode() {
    // La variable isTwelveSecondsActive ya se actualiza mediante [(ngModel)]
    await this.loadAllScenes();
    await this.handleFilterChange({
      ids: this.lastFilterIds,
      teamId: this.lastTeamFilterId,
      playModelQuery: this.lastPlayModelQuery
    });
  }

  isLoading(): boolean {
    return !(this.video && this.isSceneLoaded);
  }

  get currentSceneIndex(): number {
    if (!this.filteredScenes || this.filteredScenes.length === 0) return -1;

    if (this.currentScene) {
      return this.filteredScenes.findIndex(s => s.id === this.currentScene!.id);
    }

    const nextUpcomingIndex = this.filteredScenes.findIndex(s => s.start_time_in_seconds > this.currentTime);

    if (nextUpcomingIndex === -1) {
      return this.filteredScenes.length - 1;
    } else if (nextUpcomingIndex === 0) {
      return -1;
    } else {
      return nextUpcomingIndex - 1;
    }
  }

  get hasPreviousScene(): boolean {
    return this.currentSceneIndex > 0;
  }

  get hasNextScene(): boolean {
    return this.currentSceneIndex < this.filteredScenes.length - 1;
  }

  get canRewind5s(): boolean {
    if (!this.currentScene) return false;

    if (this.hasRewoundOnce) {
      return this.currentTime > this.currentScene.start_time_in_seconds;
    } else {
      return (this.currentScene.end_time_in_seconds - this.currentScene.start_time_in_seconds) > 5;
    }
  }

  handlePlayerReady(api: VgApiService) {
    this.playerApi = api;
  }

  handlePlayingStatus(isPlaying: boolean) {
    this.isPlaying = isPlaying;

    if (isPlaying && this.currentScene && !this.isEditingScene && this.playerApi) {
      const margin = 0.1;
      const time = this.currentTime;
      if (Math.abs(time - this.currentScene.end_time_in_seconds) <= margin) {
        this.playerApi.seekTime(this.currentScene.end_time_in_seconds + 0.1);
      }
    }
  }

  handleTimeUpdate(time: number) {
    this.currentTime = time;

    if (this.isEditingScene && this.currentScene && this.playerApi) {
      const margin = 0.1;

      if (time < this.currentScene.start_time_in_seconds) {
        this.playerApi.seekTime(this.currentScene.start_time_in_seconds);
        if (!this.isPlaying) {
          this.showEditingLockMessage("No puedes salir del inicio de la jugada mientras editas");
        }
        return;
      }

      if (time >= this.currentScene.end_time_in_seconds) {
        this.playerApi.seekTime(this.currentScene.end_time_in_seconds - margin);
        this.playerApi.pause();

        if (!this.isPlaying) {
          this.showEditingLockMessage("No puedes salir del final de la jugada mientras editas");
        }
        return;
      }
    }

    if (this.currentScene && this.isPlaying && !this.isEditingScene) {
      if (time >= this.currentScene.end_time_in_seconds) {
        // La trampa agresiva de pausa ha sido delegada a video-player.component.ts
        // para permitir transiciones suaves y continuación de jugadas.
      }
    }

    if (!this.isEditingScene) {
      if (this.manuallySelectedScene) {
        const s = this.manuallySelectedScene;
        if (time < s.start_time_in_seconds || time >= s.end_time_in_seconds) {
          this.manuallySelectedScene = null;
          this.updateCurrentScene(this.findSceneForTime(time));
        }
      } else {
        this.updateCurrentScene(this.findSceneForTime(time));
      }
    }
  }

  handleEditingStateChange(isEditing: boolean) {
    this.isEditingScene = isEditing;
  }

  private showEditingLockMessage(customMessage?: string) {
    this.snackBar.open(
      customMessage || "Termina de editar la jugada actual para continuar",
      "Cerrar",
      { duration: 3000, verticalPosition: 'top', panelClass: ['warning-snackbar'] }
    );
  }
  private findSceneForTime(time: number): VideoSceneResponse | null {
    return this.filteredScenes.find(s =>
      time >= s.start_time_in_seconds && time <= s.end_time_in_seconds
    ) || null;
  }

  private updateCurrentScene(newScene: VideoSceneResponse | null) {
    if (this.currentScene?.id === newScene?.id) return;
    this.currentScene = newScene;
    this.hasRewoundOnce = false;
    if (this.videoPlayer) {
      this.videoPlayer.resetAutoPause();
    }
  }

  activateScene(scene: VideoSceneResponse) {
    if (this.isEditingScene && scene.id !== this.currentScene?.id) {
      this.showEditingLockMessage();
      return;
    }

    if (!scene || !this.playerApi) return;
    this.manuallySelectedScene = scene;
    this.updateCurrentScene(scene);
    this.playerApi.seekTime(scene.start_time_in_seconds);
    this.playerApi.play();
  }

  onPrevScene() {
    if (this.isEditingScene) {
      this.showEditingLockMessage();
      return;
    }
    const prevIndex = this.currentSceneIndex - 1;
    if (prevIndex >= 0) {
      this.activateScene(this.filteredScenes[prevIndex]);
    }
  }

  onNextScene() {
    if (this.isEditingScene) {
      this.showEditingLockMessage();
      return;
    }
    const nextIndex = this.currentSceneIndex + 1;
    if (nextIndex < this.filteredScenes.length) {
      this.activateScene(this.filteredScenes[nextIndex]);
    }
  }

  onReplayScene() {
    if (this.currentScene) {
      this.hasRewoundOnce = false;
      this.activateScene(this.currentScene);
    }
  }

  onRewind5sScene() {
    if (!this.currentScene || !this.playerApi) return;
    this.manuallySelectedScene = this.currentScene;

    let targetTime: number;

    if (!this.hasRewoundOnce) {
      targetTime = Math.max(
        this.currentScene.start_time_in_seconds,
        this.currentScene.end_time_in_seconds - 5
      );
      this.hasRewoundOnce = true;
    } else {
      const currentTime = this.currentTime;
      const minTime = this.currentScene.start_time_in_seconds;

      if (currentTime - minTime > 5) {
        targetTime = currentTime - 5;
      } else {
        targetTime = minTime;
      }
      targetTime = Math.max(targetTime, minTime);
    }

    this.playerApi.seekTime(targetTime);
    if (!this.isPlaying) this.playerApi.play();
  }

  async handleSceneSave(updateRequest: VideoSceneUpdateRequest) {
    try {
      const updatedScene = await this.videoSceneService.updateScene(updateRequest);
      this.allScenes = this.allScenes.map(s => s.id === updatedScene.id ? updatedScene : s);

      // Mantenemos la lista filtrada actualizada
      if (this.filteredScenes !== this.allScenes) {
        this.filteredScenes = this.filteredScenes.map(s => s.id === updatedScene.id ? updatedScene : s);
      } else {
        this.filteredScenes = this.allScenes;
      }

      if (this.currentScene?.id === updatedScene.id) {
        this.currentScene = updatedScene;
        if (this.manuallySelectedScene?.id === updatedScene.id) {
          this.manuallySelectedScene = updatedScene;
        }
      }
    } catch (error) {
      console.error('Error al guardar la escena:', error);
      alert('No se pudieron guardar los cambios. Inténtalo de nuevo.');
    }
  }

  handleDeleteRequest(sceneToDelete: VideoSceneResponse): void {
    const dialogRef = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
      width: '450px',
      data: {
        title: 'Confirmar Eliminación',
        message: `¿Estás seguro de que deseas eliminar la jugada "${sceneToDelete.label}"?`,
        confirmButtonText: 'Eliminar',
      },
    });

    dialogRef.afterClosed().pipe(filter(result => result === true)).subscribe(async () => {
      try {
        if (this.video?.id) {
          const deletedIndex = this.filteredScenes.findIndex(s => s.id === sceneToDelete.id);

          // 1. Borramos en Backend (esto dispara el re-numerado en servidor)
          await this.videoSceneService.deleteScene(sceneToDelete.id, this.video.id);

          // 2. IMPORTANTE: Recargamos todas las escenas "crudas" para tener los nuevos labels (1, 2, 3...)
          await this.loadAllScenes();

          // 3. IMPORTANTE: Re-ejecutamos el filtro actual.
          // Esto actualizará 'this.filteredScenes' con los datos frescos y etiquetas corregidas.
          await this.handleFilterChange({
            ids: this.lastFilterIds,
            teamId: this.lastTeamFilterId,
            playModelQuery: this.lastPlayModelQuery
          });

          // 4. Gestión de selección (intentar mantenernos en el mismo índice)
          if (this.currentScene?.id === sceneToDelete.id) {
            this.updateCurrentScene(null);
          }
          if (this.manuallySelectedScene?.id === sceneToDelete.id) {
            this.manuallySelectedScene = null;
          }

          if (this.filteredScenes.length > 0 && deletedIndex !== -1) {
            // Intentamos ir a la jugada que ahora ocupa ese hueco (o la anterior si era la última)
            const targetIndex = Math.min(deletedIndex, this.filteredScenes.length - 1);
            const targetScene = this.filteredScenes[targetIndex];

            if (targetScene) {
              this.activateScene(targetScene);
            }
          }

          this.snackBar.open('Jugada eliminada y lista actualizada', 'OK', { duration: 3000 });
        }
      } catch (error) {
        console.error('Error al eliminar la jugada:', error);
        alert('No se pudo eliminar la jugada.');
      }
    });
  }

  async exportCsv(): Promise<void> {
    if (!this.video?.id) return;
    try {
      const response = await this.videoSceneService.exportScenesToCsv(this.video.id, this.isTwelveSecondsActive);
      const blob = new Blob([response.body as BlobPart], { type: 'text/csv; charset=utf-8' });
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'jugadas.csv';
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
      }
      saveAs(blob, filename);
    } catch (err) {
      console.error('Error al exportar CSV:', err);
      alert('Error al exportar las jugadas.');
    }
  }

  private async loadVideo() {
    const videoId = this.route.snapshot.paramMap.get('id');
    if (videoId) {
      this.video = await this.videoStreamingService.getVideoById(videoId);
    }
  }

  goBack() {
    this.router.navigate(['/analysis']);
  }

  private async loadSeasons(): Promise<void> {
    try {
      this.seasons = await firstValueFrom(this.statisticsService.getSeasonsEndpoint());
    } catch (error) {
      console.error('Error cargando temporadas para edición de vídeo', error);
      this.seasons = [];
    }
  }

  private async loadVideoBasicInfo(videoId: string): Promise<void> {
    try {
      const basicInfo = await this.videoStreamingService.getVideoBasicInfo(videoId);
      if (!this.video) return;
      this.video = {
        ...this.video,
        title: basicInfo.title,
        season_id: basicInfo.season_id,
        match_date: basicInfo.match_date,
        gender: basicInfo.gender || this.video.gender,
        competition: this.resolveOptionName(this.allowedCompetitionOptions, basicInfo.competition_id) || this.video.competition,
        category: this.resolveOptionName(this.allowedCategoryOptions, basicInfo.category_id) || this.video.category
      };
    } catch (error) {
      console.error('Error cargando datos básicos del vídeo', error);
    }
  }

  private async loadAllScenes() {
    if (!this.video?.id) return;
    try {
      this.allScenes = await this.videoSceneService.getScenes(this.video.id, this.isTwelveSecondsActive);
    } catch (error) {
      console.error("Error al cargar escenas:", error);
      this.allScenes = [];
    }
  }

  private syncDefaultTeamFilterOptions(): void {
    if (this.availableTeams.length > 0 || this.allScenes.length === 0) {
      return;
    }

    const hasDefaultTeam1 = this.allScenes.some(scene =>
      scene.default_attacking_team_id === 1 || scene.default_defending_team_id === 1
    );
    const hasDefaultTeam2 = this.allScenes.some(scene =>
      scene.default_attacking_team_id === 2 || scene.default_defending_team_id === 2
    );

    const fallbackTeams: TeamOptionResponse[] = [];
    if (hasDefaultTeam1) {
      fallbackTeams.push({ id: '1', name: 'Equipo 1' });
    }
    if (hasDefaultTeam2) {
      fallbackTeams.push({ id: '2', name: 'Equipo 2' });
    }

    if (fallbackTeams.length > 0) {
      this.availableTeams = fallbackTeams;
    }
  }

  private async loadAllowedVideoFilters(): Promise<void> {
    try {
      const filters = await firstValueFrom(this.filterDataService.getVideoFilters());
      this.allowedCompetitionOptions = (filters?.competitions || []).map(c => ({ id: c.id, name: c.name }));
      this.allowedCategoryOptions = (filters?.categories || []).map(c => ({ id: c.id, name: c.name }));
      this.allowedGenderOptions = (filters?.genders || []).map(g => ({ id: g.id, name: g.name }));
    } catch (error) {
      console.error('Error cargando filtros de vídeo permitidos', error);
      this.allowedCompetitionOptions = [];
      this.allowedCategoryOptions = [];
      this.allowedGenderOptions = [];
    }
  }

  private resolveOptionName(options: { id: number; name: string }[], id: number | null | undefined): string | null {
    if (id === null || id === undefined) return null;
    return options.find(opt => opt.id === id)?.name ?? null;
  }

  async handleFilterChange(event: FilterChangeEvent): Promise<void> {
    const { ids: selectedIds, teamId, playModelQuery } = event;

    this.lastFilterIds = selectedIds;
    this.lastTeamFilterId = teamId;
    this.lastPlayModelQuery = playModelQuery;

    this.updateDisplayedActiveFilters(selectedIds, teamId, playModelQuery);
    this.manuallySelectedScene = null;

    if (selectedIds.length === 0 && !teamId && !playModelQuery) {
      this.filteredScenes = this.allScenes;
    } else {
      try {
        if (this.video?.id) {
          // Usamos el método que soporta el filtrado de equipo
          this.filteredScenes = await this.videoSceneService.getFilteredScenes(
            this.video.id,
            selectedIds,
            teamId,
            playModelQuery,
            this.isTwelveSecondsActive
          );
        }
      } catch (error) {
        console.error("Error al filtrar escenas:", error);
        this.filteredScenes = [];
      }
    }
    this.updateCurrentScene(this.findSceneForTime(this.currentTime));
  }

  private updateDisplayedActiveFilters(selectedIds: number[], teamId: string | null, playModelQuery: string): void {
    this.displayedActiveFilters = [];

    // 1. Filtro de Equipo
    if (teamId && this.availableTeams.length > 0) {
      const team = this.availableTeams.find(t => t.id === teamId);
      if (team) {
        this.displayedActiveFilters.push({
          id: -999, // ID dummy para UI
          categoryDisplay: 'Equipo',
          valueDisplay: team.name
        });
      }
    }

    if (playModelQuery) {
      this.displayedActiveFilters.push({
        id: -998,
        categoryDisplay: 'Modelo Juego',
        valueDisplay: playModelQuery
      });
    }

    // 2. Filtros de Jugadas
    if (!selectedIds.length || !this.playModelOptions) return;
    const idSet = new Set(selectedIds);

    for (const key in this.playModelOptions) {
      const options = this.playModelOptions[key];
      if (Array.isArray(options)) {
        for (const opt of options) {
          if (idSet.has(opt.id)) {
            this.displayedActiveFilters.push({
              id: opt.id,
              categoryDisplay: key,
              valueDisplay: opt.display || '?'
            });
          }
        }
      }
    }
  }

  handleRemoveFilter(idToRemove: number): void {
    if (idToRemove === -999) {
      // Es el filtro de equipo
      this.sceneFilter?.removeTeamFilter();
    } else if (idToRemove === -998) {
      this.sceneFilter?.removePlayModelQuery();
    } else {
      this.sceneFilter?.removeFilterById(idToRemove);
    }
  }

  public onRelaunchPipeline() {
    if (!this.video?.id || this.isRelaunching) return;

    if (!confirm('¿Estás seguro de relanzar la IA para este vídeo?')) {
      return;
    }

    this.isRelaunching = true;
    this.executeRelaunch(this.video.id, false);
  }

  private executeRelaunch(videoId: string, forceRestart: boolean): void {
    this.videoLabService.relaunchPipeline(videoId, forceRestart).subscribe({
      next: (res: RelaunchPipelineResponse) => {
        if (res.status === 'needs_confirmation' && !forceRestart) {
          this.isRelaunching = false;
          const shouldForce = confirm(
            'El vídeo está actualmente en PROCESSING en el orquestador.\n¿Deseas pararlo y relanzarlo ahora?'
          );
          if (!shouldForce) {
            return;
          }
          this.isRelaunching = true;
          this.executeRelaunch(videoId, true);
          return;
        }

        const isSkipped = res.status === 'skipped';
        const icon = isSkipped ? '⚠️ ' : '✅ ';

        this.snackBar.open(icon + res.message, 'OK', {
          duration: 5000,
          panelClass: isSkipped ? ['warning-snackbar'] : ['success-snackbar']
        });

        this.isRelaunching = false;
      },
      error: (err) => {
        console.error(err);
        const errorMsg = err.error?.detail || 'Error desconocido al contactar con el servidor';
        this.snackBar.open(`❌ Error: ${errorMsg}`, 'Cerrar', { duration: 5000 });
        this.isRelaunching = false;
      }
    });
  }
}
